const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/utils/orderUtils.ts';
import Imm from 'immutable';
import { compareStringWNumber } from './stringUtils';
import _ from 'lodash';
import { getOrderId, getRefId, isValidRefId } from './orderHotspotEventUtils';

/**
 * Comparator function functions to sort orders by NAME, non-ghost orders after ghost orders, STARTDAT and ORDERNR.
 * Ghost orders are used to build the Timeline on ChartPage.
 * @param orderA
 * @param orderB
 * @return number
 */
export function sortOrdersBasic(orderA: Imm.Map<string, any>, orderB: Imm.Map<string, any>) {
    let sortResult = compareStringWNumber(orderA.get('NAME'), orderB.get('NAME'));
    if (sortResult !== 0) {
        return sortResult;
    }

    // make sure ghost orders with the same name as other orders are below those orders
    if (!!orderA.get('isGhostOrder') !== !!orderB.get('isGhostOrder')) {
        return orderA.get('isGhostOrder') ? 1 : -1;
    }

    sortResult = compareStringWNumber(orderA.get('STARTDAT'), orderB.get('STARTDAT'));
    if (sortResult !== 0) {
        return sortResult;
    }

    return compareStringWNumber(orderA.get('ORDERNR'), orderB.get('ORDERNR'));
}

/**
 * Return all orders which should be displayed in the same row according to the given mapping orderToSuccessor
 * @param orderToSubsequentOrder Mapping between one order and the next one which is displayed in the same row
 * @param orderId Id of the preceding order
 * @return An array of orderIds which might be displayed in the same row as orderId
 */
function getSubsequentOrders(orderToSubsequentOrder: Imm.Map<string, string>, orderId: string): string[] {
    let retrievedOrderIds = Imm.Set<string>();

    const getNextSuccessor = (orderId: string): string[] => {
        retrievedOrderIds = retrievedOrderIds.add(orderId);
        const nextOrder = orderToSubsequentOrder.get(orderId);
        if (orderId && nextOrder && !retrievedOrderIds.has(nextOrder)) {
            return [nextOrder].concat(getNextSuccessor(nextOrder));
        }
        return [];
    };

    return ([] as string[]).concat(getNextSuccessor(orderId)).filter((orderId: string) => !_.isEmpty(orderId));
}

/**
 * Comparator function (according to immutableJS) to sort orders by category (if respected), name, start date and
 * ordernr.
 * @param orderA
 * @param orderB
 * @param respectCategory
 * @param placeMedicationOrdersAtEnd  When true, medication orders are sorted after other kind of orders.
 *     This is required for medication orders which refer to wounds.
 * @return number
 */
export const sortOrders = (
    orderA: Imm.Map<string, any>,
    orderB: Imm.Map<string, any>,
    respectCategory = true,
    placeMedicationOrdersAtEnd = false
): number => {
    let sortResult: number;

    // First check if the category is sorted correctly
    if (respectCategory) {
        sortResult = orderA.getIn(['grouping', 'categorySortIdx']) - orderB.getIn(['grouping', 'categorySortIdx']);
        if (sortResult !== 0) {
            return sortResult;
        }
    }

    // Sort groups after orders
    sortResult = groupsAfterOrders(orderA, orderB);
    if (sortResult !== 0) {
        return sortResult;
    }

    // Sort medis orders at the end
    if (placeMedicationOrdersAtEnd) {
        sortResult = (orderA.get('KAT') === 'MEDI' ? 1 : 0) - (orderB.get('KAT') === 'MEDI' ? 1 : 0);
        if (sortResult !== 0) {
            return sortResult;
        }
    }

    if (
        orderHasToBeSortedByDate(orderA) &&
        orderHasToBeSortedByDate(orderB) &&
        isValidRefId(orderA.getIn(['grouping', 'parentsOrderId'])) &&
        isValidRefId(orderB.getIn(['grouping', 'parentsOrderId']))
    ) {
        // Orders which refer to an installation have to be sorted primary by start date
        sortResult = compareStringWNumber(orderA.get('STARTDAT'), orderB.get('STARTDAT'));
        if (sortResult !== 0) {
            return sortResult;
        }

        sortResult = compareStringWNumber(orderA.get('NAME'), orderB.get('NAME'));
        if (sortResult !== 0) {
            return sortResult;
        }

        // make sure ghost orders with the same name as other orders are below those orders
        if (!!orderA.get('isGhostOrder') !== !!orderB.get('isGhostOrder')) {
            return orderA.get('isGhostOrder') ? 1 : -1;
        }

        sortResult = compareStringWNumber(orderA.get('ORDERNR'), orderB.get('ORDERNR'));
    } else {
        sortResult = sortOrdersBasic(orderA, orderB);
    }
    return sortResult;
};

/**
 * Helper function to sort orders. Groups are placed after 'normal' orders.
 * @param recA
 * @param recB
 * @returns {number}
 */
export function groupsAfterOrders(recA: Imm.Map<string, any>, recB: Imm.Map<string, any>): number {
    const orderAIsGroup = recA.get('KAT') === 'ORDGROUP' ? 1 : 0;
    const orderBIsGroup = recB.get('KAT') === 'ORDGROUP' ? 1 : 0;
    return orderAIsGroup - orderBIsGroup;
}

/**
 * Build a lookup which connects orders which might be displayed on the same row.
 * @param orderCollection  Map of orders for which a map of subsequent orders has to be created
 * @param putGhostOrdersOnSeparateLine If true, ghost orders will never be displayed on top of an order
 *     but in a separate line.
 * @param ignoreGhostOrdersRefId A ghost order's Ref Id is the reference of an event to its order.
 * The ghost order actually is a replacement of this order because it is not available. If there is no
 * reference, the event was not documented in connection to an order.
 * @return A map of orderIds which map orders with its possible subsequent order.
 */
export function mapSubsequentOrders(
    orderCollection: Imm.Map<string, any>,
    putGhostOrdersOnSeparateLine = true,
    ignoreGhostOrdersRefId = false
): Imm.Map<string, string> {
    /**
     * Sort the orders by name and start time first is required to the the correct result and find a successor quicker.
     */
    const sortedOrderCollection = orderCollection.sort(sortOrdersBasic);

    let orderToSubsequentOrder = Imm.Map<string, string>();
    let handledOrderIds = Imm.Set<string>();

    /**
     * non ghost orders which are on the current line
     * - will be reset if there is an order which does not follow the existing orders
     * - used for estimating subsequent orders
     */
    let nonGhostOrdersOnCurrentLine = Imm.Set<string>();

    /**
     * All orders which are succeeding an other order. Used to ensure that each order can have at maximum one successor.
     */
    let allSucceedingOrders = Imm.Set<string>();
    /**
     * Check if possibleSuccessor might be on the same line as order.
     * @param order
     * @param possibleSuccessor
     * @returns boolean
     */
    const areTheseOrdersOnTheSameLine = (
        order: Imm.Map<string, any>,
        possibleSuccessor: Imm.Map<string, any>
    ): boolean => {
        let result = false;
        if (order && possibleSuccessor && getOrderId(order) !== getOrderId(possibleSuccessor)) {
            // Because of the high number of if-clauses, the nesting of if-clauses was preferred over a compact code style.
            if (order.get('NAME') === possibleSuccessor.get('NAME')) {
                if (order.get('STOPDAT') <= possibleSuccessor.get('STARTDAT')) {
                    result = true;
                } else {
                    // time ranges of the orders overlap
                    if (!order.get('isGhostOrder') && possibleSuccessor.get('isGhostOrder')) {
                        if (ignoreGhostOrdersRefId) {
                            return true;
                        } else {
                            // Put ghost order on top of a normal order if the event of the ghost order does not refer to an order (if the event
                            // refers to an order, then the referred order is not order for sure.
                            if (possibleSuccessor.get('eventRefId') === '') {
                                result = true;
                            }
                        }
                    }
                }

                if (result && (order.get('isGhostOrder') || possibleSuccessor.get('isGhostOrder'))) {
                    if (putGhostOrdersOnSeparateLine) {
                        result = order.get('isGhostOrder') === possibleSuccessor.get('isGhostOrder');
                    } else {
                        if (possibleSuccessor.get('eventRefId') === '') {
                            result = true;
                        } else {
                            if (order.get('isGhostOrder') && possibleSuccessor.get('isGhostOrder')) {
                                /**
                                 * It is necessary to check preceding orders for the following case: There is an order and
                                 * multiple ghost orders which are possible successors. Because the ghost orders do not
                                 * overlap, ghost orders are usually in the same line, even when the events of some ghost
                                 * orders refer to an unavailable order. However, if there is a preceding normal order
                                 * (ORDER1), the ghost orders with a reference must not overly other orders (GHOST2 is a
                                 * successor of GHOST1 but must not overlap ORDER1 if GHOST2 has a reference)! In other
                                 * words: GHOST2 must be a possible successor of each non-ghost order of the current line.
                                 *
                                 * =============| ORDER1 =============|
                                 *    |=GHOST1=|      |=GHOST2=|
                                 */
                                if (nonGhostOrdersOnCurrentLine.isEmpty()) {
                                    result = true;
                                } else {
                                    result = nonGhostOrdersOnCurrentLine.reduce(
                                        (_lastResult: boolean, nonGhostOrderId: string) => {
                                            return areTheseOrdersOnTheSameLine(
                                                sortedOrderCollection.get(nonGhostOrderId),
                                                possibleSuccessor
                                            );
                                        },
                                        true
                                    );
                                }
                            } else {
                                result = false;
                            }
                        }
                    }
                }
            }

            // this checks the referenced orders (i. e. parent infusion orders -> there are more than one orders if the order and the
            // parent have a state change)
            if (result && !possibleSuccessor.get('isGhostOrder')) {
                result =
                    getRefId(order) === getRefId(possibleSuccessor) ||
                    areTheseOrdersOnTheSameLine(
                        sortedOrderCollection.get(getRefId(order)),
                        sortedOrderCollection.get(getRefId(possibleSuccessor))
                    );
            }
        }
        return result;
    };

    sortedOrderCollection.forEach((order: Imm.Map<string, any>) => {
        let orderId = getOrderId(order) as string;
        let foundNewSuccessor = false;

        if (!handledOrderIds.has(orderId)) {
            do {
                foundNewSuccessor = false;
                handledOrderIds = handledOrderIds.add(orderId);
                const subsequentOrder = sortedOrderCollection.find((possibleSuccessor: Imm.Map<string, any>) => {
                    return (
                        !allSucceedingOrders.has(getOrderId(possibleSuccessor) as string) &&
                        areTheseOrdersOnTheSameLine(order, possibleSuccessor)
                    );
                });
                if (
                    subsequentOrder &&
                    !subsequentOrder.isEmpty() &&
                    orderToSubsequentOrder.get(subsequentOrder) !== orderId
                ) {
                    // subsequentOrder is a successor of order!
                    orderToSubsequentOrder = orderToSubsequentOrder.set(orderId, getOrderId(subsequentOrder) as string);
                    allSucceedingOrders = allSucceedingOrders.add(getOrderId(subsequentOrder) as string);
                    if (!order.get('isGhostOrder')) {
                        nonGhostOrdersOnCurrentLine = nonGhostOrdersOnCurrentLine.add(orderId);
                    }
                    if (!subsequentOrder.get('isGhostOrder')) {
                        nonGhostOrdersOnCurrentLine = nonGhostOrdersOnCurrentLine.add(
                            getOrderId(subsequentOrder) as string
                        );
                    }

                    // for next loop
                    foundNewSuccessor = true;
                    orderId = getOrderId(subsequentOrder) as string;
                    order = sortedOrderCollection.get(orderId);
                } else {
                    // subsequentOrder is NOT a successor of order!
                    nonGhostOrdersOnCurrentLine = nonGhostOrdersOnCurrentLine.clear();
                }
            } while (foundNewSuccessor);
        }
    });
    return orderToSubsequentOrder;
}

/**
 * Some orders have to be sorted by date instead of the normal criteria. Test if this is the case for order.
 * @param order
 * @return boolean
 */
function orderHasToBeSortedByDate(order: Imm.Map<string, any>): boolean {
    const fkey = order.get('FKEY');
    return (fkey !== 'INST' && fkey.indexOf('INST') === 0) || (fkey !== 'WUND' && fkey.indexOf('WUND') === 0);
}

/**
 * Build a list of rows. Each row contains one or more orders.
 * @param ordersToAdd A map containing all top-level orders to add to the
 *     list. Children of these orders should not be contained in here but in allOrders. This map will be processed
 *     order by order to build the rows. Children will be loaded from the (unsorted) allOrders.
 * @param allOrders All orders which might be added to the rows. allOrders
 *     might be unsorted but must contain children of orders of topLevelOrdersToAdd
 *     (non-top-level orders) different to ordersToAdd. Orders of allOrders will be added if
 *        <li>the order is also in topLevelOrdersToAdd</li>
 *        <li>the order is a children or further descendant of an added order</li>
 * @param orderToSuccessor Mapping between one order and the next one which is displayed in the same row
 * @param ordersGroupedByParent Orders grouped by the orderId of its parent. If
 *     ordersGroupedByParent is not provided or empty, then nesting of orders is not considered.
 * @param placeMedicationOrdersAtEnd When true, medication orders are sorted after other kind of orders.
 *     This is required for medication orders which refer to wounds.
 */
export function buildRowsOfOrders(
    ordersToAdd: Imm.Map<string, Imm.Map<string, any>>,
    allOrders: Imm.Map<string, Imm.Map<string, any>>,
    orderToSuccessor = Imm.Map<string, string>(),
    ordersGroupedByParent = Imm.Map<string, any>(),
    placeMedicationOrdersAtEnd = false
): Imm.List<Imm.List<Imm.Map<string, any>>> {
    let rowList = Imm.List<Imm.List<Imm.Map<string, any>>>(); // list of rows
    const ordersAddedToList: string[] = []; // This array contains the orderIds of all orders which have been added to
    // rowList. It is used to make sure, that orders are added to the list only once.
    const hasOrderBeenAdded = (orderId: string) => ordersAddedToList.includes(orderId);

    /**
     * Add order and its subsequent orders (orders which should be displayed in the same row) to a row and push this
     * row to the list of rows. Children of the handled orders will be added in the next rows to the list as well. When
     * adding the children orders, the possible parents of children orders have to be considered to make sure that
     * children are not combined with nonchildren in the same line.
     * @param order Order to add to the row.
     * @param possibleParents If there are possible parents, then order is a child of one of that
     *     parents. Other orders, which might be put on the same line, must also be a child of these possible parents.
     */
    const addOrderToRow = (order: Imm.Map<string, any>, possibleParents = Imm.Set<string>()) => {
        const orderId = getOrderId(order);
        if (!orderId) {
            return;
        }
        let ordersForNextRows = Imm.Map<string, any>();
        let parentsOfNextRows = Imm.Set<string>();

        if (!hasOrderBeenAdded(orderId)) {
            // the order is not in the list yet
            let row = Imm.List<Imm.Map<string, any>>();

            // add successive orders to the current row
            const succOrders = [orderId, ...getSubsequentOrders(orderToSuccessor, orderId)];
            succOrders.forEach((orderIdToAdd: string) => {
                if (
                    !_.isEmpty(orderIdToAdd) &&
                    !hasOrderBeenAdded(orderIdToAdd) &&
                    (possibleParents.isEmpty() || possibleParents.has(getRefId(order)))
                    // Orders might be on the same line according to name and time. But such matching orders could be part
                    // of grouped items and if they are not grouped below the same parents, they should not be displayed in
                    // the same row.
                ) {
                    row = row.push(allOrders.get(orderIdToAdd));
                    ordersAddedToList.push(orderIdToAdd);
                    if (ordersGroupedByParent.has(orderIdToAdd)) {
                        ordersForNextRows = ordersForNextRows.merge(ordersGroupedByParent.get(orderIdToAdd));
                        parentsOfNextRows = parentsOfNextRows.add(orderIdToAdd);
                    }
                }
            });

            // add row to list
            if (!row.isEmpty()) {
                rowList = rowList.push(row);
                row = row.clear();
            }

            // handle child orders of orders which have just been added to the list
            ordersForNextRows
                .sort((orderA: Imm.Map<string, any>, orderB: Imm.Map<string, any>) =>
                    sortOrders(orderA, orderB, false, placeMedicationOrdersAtEnd)
                )
                .forEach((order: Imm.Map<string, any>) => addOrderToRow(order, parentsOfNextRows));
        }
    };

    ordersToAdd.forEach((order: Imm.Map<string, any>) => addOrderToRow(order));
    return rowList;
}

export function getHotspotsTimes(orders: Imm.Map<string, any> = Imm.Map<string, any>()): Imm.Set<string> {
    return orders.reduce((times: Imm.Set<string>, order: Imm.Map<string, any>) => {
        const applications = order.get('Applications');
        if (applications && !applications.isEmpty()) {
            applications.forEach((hotspot: Imm.Map<string, any>) => {
                times = times.add(hotspot.get('DAT'));
            });
        }
        return times;
    }, Imm.Set<string>());
}

export const orderTagKind = (order: Imm.Map<string, any>) => {
    const hasComment =
        (order.hasIn(['CONTENT', 'Bemerkung']) && !!order.getIn(['CONTENT', 'Bemerkung'])) || !!order.get('COMM');
    const hasAttentionFlag = order.hasIn(['CONTENT', 'AttentionFlag']) && !!order.getIn(['CONTENT', 'AttentionFlag']);
    if (hasComment && hasAttentionFlag) {
        return 'comment-flag';
    } else if (hasAttentionFlag) {
        return 'flag';
    } else if (hasComment) {
        return 'comment';
    }
    return undefined;
};
